// src/pages/sqf/SqfPolicyEditorPage.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import RichTextEditor from '../../components/RichTextEditor';
import sqfService from '../../services/sqfService';
import sqfAiService from '../../services/sqfAiService';
import { useLanguage, t } from '../../context/LanguageContext';

const CATEGORY_LABELS = {
  food_safety_plan: 'Food Safety Plan', ssop: 'SSOP', sop: 'SOP',
  prerequisite_program: 'Prerequisite Program', haccp: 'HACCP',
  monitoring: 'Monitoring', corrective_action: 'Corrective Action',
  verification: 'Verification', training: 'Training', recall: 'Recall',
  pest_control: 'Pest Control', allergen: 'Allergen',
  crisis_management: 'Crisis Management', supplier: 'Supplier',
  contractor: 'Contractor', visitor: 'Visitor', vendor: 'Vendor', other: 'Other',
};

export default function SqfPolicyEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Draft version being edited
  const [draftVersion, setDraftVersion] = useState(null);
  const [editorContent, setEditorContent] = useState('');
  const [editorContentEs, setEditorContentEs] = useState('');
  const [editorLang, setEditorLang] = useState('en');
  const [revisionDate, setRevisionDate] = useState('');
  const [versionNotes, setVersionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(null); // holds version to restore
  const [discarding, setDiscarding] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateDone, setTranslateDone] = useState('');

  // Policy metadata editing
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaName, setMetaName] = useState('');
  const [metaNameEs, setMetaNameEs] = useState('');
  const [metaCategory, setMetaCategory] = useState('other');
  const [metaDesc, setMetaDesc] = useState('');
  const [metaSqfChapterRef, setMetaSqfChapterRef] = useState('');
  const [metaDocumentLanguage, setMetaDocumentLanguage] = useState('en');
  const [savingMeta, setSavingMeta] = useState(false);

  const saveTimer = useRef(null);

  const handleImageUpload = useCallback(async (file) => {
    const API_URL = process.env.REACT_APP_API_URL || '';
    const { uploadUrl, publicUrl } = await fetch(`${API_URL}/api/documents/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ fileName: file.name, contentType: file.type, fileSize: file.size }),
    }).then(r => r.json());
    await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
    return publicUrl;
  }, []);

  const load = () => {
    setLoading(true);
    sqfService.getPolicy(id)
      .then(p => {
        setPolicy(p);
        setMetaName(p.name);
        setMetaNameEs(p.nameEs || '');
        setMetaCategory(p.category);
        setMetaDesc(p.description || '');
        setMetaSqfChapterRef(p.sqfChapterRef || '');
        setMetaDocumentLanguage(p.documentLanguage || 'en');
        const versions = p.Versions || [];
        const drafts = versions.filter(v => v.status === 'draft');
        const draft = drafts.length > 0 ? drafts.reduce((a, b) => b.versionNumber > a.versionNumber ? b : a) : null;
        const published = versions.find(v => v.id === p.activeVersionId);
        // Always sync draft state — critical after publish so stale draftVersion is cleared
        setDraftVersion(draft || null);
        // If draft exists but is empty, fall back to published content so the user never starts from blank
        setEditorContent(draft ? (draft.content || published?.content || '') : '');
        setEditorContentEs(draft ? (draft.contentEs || published?.contentEs || '') : '');
        setRevisionDate(draft ? draft.revisionDate || '' : '');
        setVersionNotes(draft ? draft.notes || '' : '');
      })
      .catch(() => setError(t('Failed to load policy.', 'Error al cargar la política.', lang)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save draft 2s after last change
  const scheduleAutoSave = () => {
    if (!draftVersion) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveDraft(), 2000);
  };

  const handleContentChange = (value) => {
    if (editorLang === 'es') {
      setEditorContentEs(value);
    } else {
      setEditorContent(value);
    }
    setSaved(false);
    scheduleAutoSave();
  };

  const saveDraft = async (overrideEn, overrideEs) => {
    if (!draftVersion) return;
    setSaving(true);
    try {
      await sqfService.updatePolicyVersion(id, draftVersion.id, {
        content: overrideEn !== undefined ? overrideEn : editorContent,
        contentEs: overrideEs !== undefined ? overrideEs : editorContentEs,
        revisionDate: revisionDate || null,
        notes: versionNotes || null,
      });
      setSaved(true);
    } catch {
      setError(t('Auto-save failed.', 'Error al guardar automáticamente.', lang));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDraft = async () => {
    setSaving(true);
    try {
      // Pre-populate from published version so the user sees existing content
      const versions = policy?.Versions || [];
      const published = versions.find(v => v.id === policy?.activeVersionId);
      const version = await sqfService.createPolicyVersion(id, {
        revisionDate: published?.revisionDate || null,
        content: published?.content || '',
        contentEs: published?.contentEs || '',
        notes: null,
      });
      setDraftVersion(version);
      setEditorContent(version.content || '');
      setEditorContentEs(version.contentEs || '');
      setSaved(false);
    } catch {
      setError(t('Failed to create draft.', 'Error al crear el borrador.', lang));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMeta = async () => {
    setSavingMeta(true);
    try {
      const updated = await sqfService.updatePolicy(id, {
        name: metaName,
        nameEs: metaNameEs || null,
        category: metaCategory,
        description: metaDesc,
        sqfChapterRef: metaSqfChapterRef || null,
        documentLanguage: metaDocumentLanguage,
      });
      setPolicy(p => ({ ...p, name: updated.name, nameEs: updated.nameEs, category: updated.category, description: updated.description, sqfChapterRef: updated.sqfChapterRef, documentLanguage: updated.documentLanguage }));
      setEditingMeta(false);
    } catch {
      setError(t('Failed to save changes.', 'Error al guardar los cambios.', lang));
    } finally {
      setSavingMeta(false);
    }
  };

  const handlePublish = () => {
    if (!draftVersion) return;
    setShowPublishConfirm(true);
  };

  const confirmPublish = async () => {
    setShowPublishConfirm(false);
    clearTimeout(saveTimer.current);
    setPublishing(true);
    try {
      await sqfService.updatePolicyVersion(id, draftVersion.id, {
        content: editorContent,
        contentEs: editorContentEs,
        revisionDate: revisionDate || null,
        notes: versionNotes || null,
      });
      await sqfService.publishPolicyVersion(id, draftVersion.id);
      load();
    } catch {
      setError(t('Failed to publish.', 'Error al publicar.', lang));
    } finally {
      setPublishing(false);
    }
  };

  const handleArchive = () => setShowArchiveConfirm(true);

  const confirmArchive = async () => {
    setShowArchiveConfirm(false);
    try {
      await sqfService.archivePolicy(id);
      navigate('/sqf/policies');
    } catch {
      setError(t('Failed to archive.', 'Error al archivar.', lang));
    }
  };

  const handleDiscardDraft = () => setShowDiscardConfirm(true);

  const confirmDiscard = async () => {
    if (!draftVersion) return;
    setShowDiscardConfirm(false);
    setDiscarding(true);
    try {
      await sqfService.deletePolicyVersion(id, draftVersion.id);
      load();
    } catch {
      setError(t('Failed to discard draft.', 'Error al descartar el borrador.', lang));
    } finally {
      setDiscarding(false);
    }
  };

  if (loading) return <div className="container mt-5 text-center text-muted"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>;

  const publishedVersions = (policy?.Versions || []).filter(v => v.status === 'published');
  const activeVersion = publishedVersions.find(v => v.id === policy?.activeVersionId);

  const handleTranslate = async (targetLang) => {
    if (!policy?.activeVersionId) return;
    setTranslating(true);
    setTranslateDone('');
    try {
      await sqfAiService.translateEntity({ entityType: 'policy', entityId: policy.id, targetLanguage: targetLang });
      // Reload so the new bilingual draft and updated policy name appear
      load();
      setEditorLang(targetLang);
      setTranslateDone(t(
        `Translation to ${targetLang === 'es' ? 'Spanish' : 'English'} added as a new draft. Switch the language tab to review it.`,
        `Traducción al ${targetLang === 'es' ? 'español' : 'inglés'} agregada como nuevo borrador. Cambia la pestaña de idioma para revisarla.`,
        lang
      ));
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('Translation failed.', 'La traducción falló.', lang));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="container mt-4 pb-5" style={{ maxWidth: 860 }}>
      {/* Header */}
      <div className="d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf/policies" className="text-muted" style={{ fontSize: 12 }}>← {t('Policies', 'Políticas', lang)}</Link>
          {editingMeta ? (
            <div className="mt-1 d-flex flex-column gap-2" style={{ maxWidth: 520 }}>
              <input className="form-control form-control-sm fw-semibold" value={metaName} onChange={e => setMetaName(e.target.value)} placeholder="🇺🇸 Policy name" style={{ fontSize: 16, fontWeight: 600 }} />
              <input className="form-control form-control-sm" value={metaNameEs} onChange={e => setMetaNameEs(e.target.value)} placeholder="🇪🇸 Nombre de la política (opcional)" />
              <div className="d-flex gap-2">
                <select className="form-select form-select-sm" style={{ maxWidth: 180 }} value={metaCategory} onChange={e => setMetaCategory(e.target.value)}>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select className="form-select form-select-sm" style={{ maxWidth: 130 }} value={metaDocumentLanguage} onChange={e => setMetaDocumentLanguage(e.target.value)}>
                  <option value="en">🇺🇸 English</option>
                  <option value="es">🇪🇸 Español</option>
                  <option value="both">🇺🇸🇪🇸 Both</option>
                </select>
                <input className="form-control form-control-sm" placeholder="SQF Ref (e.g. 2.4.2)" value={metaSqfChapterRef} onChange={e => setMetaSqfChapterRef(e.target.value)} style={{ maxWidth: 120 }} />
              </div>
              <input className="form-control form-control-sm" placeholder={t('Description (optional)', 'Descripción (opcional)', lang)} value={metaDesc} onChange={e => setMetaDesc(e.target.value)} />
              <div className="d-flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={handleSaveMeta} disabled={savingMeta}>{savingMeta ? t('Saving…', 'Guardando…', lang) : t('Save', 'Guardar', lang)}</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingMeta(false)}>{t('Cancel', 'Cancelar', lang)}</button>
              </div>
            </div>
          ) : (
            <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
              <h4 className="mb-0">{(lang === 'es' && policy?.nameEs) ? policy.nameEs : policy?.name}</h4>
              <span className="badge bg-primary" style={{ fontSize: 11 }}>
                {CATEGORY_LABELS[policy?.category] || policy?.category}
              </span>
              {policy?.sqfChapterRef && (
                <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>§ {policy.sqfChapterRef}</span>
              )}
              {policy?.documentLanguage && policy.documentLanguage !== 'en' && (
                <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                  {policy.documentLanguage === 'es' ? '🇪🇸 ES' : '🇺🇸🇪🇸 Both'}
                </span>
              )}
              {policy?.aiGenerated && (
                <span className="badge bg-light text-dark border" style={{ fontSize: 11 }} title={t('Generated by AI', 'Generado por IA', lang)}>🤖 AI</span>
              )}
              <button className="btn btn-link btn-sm p-0 text-muted" style={{ fontSize: 12 }} onClick={() => setEditingMeta(true)}>{t('Edit', 'Editar', lang)}</button>
            </div>
          )}
          {policy?.description && !editingMeta && <div className="text-muted mt-1" style={{ fontSize: 13 }}>{policy.description}</div>}
        </div>
        <div className="d-flex gap-2">
          {policy?.activeVersionId && (
            <span className="badge bg-success align-self-center">
              {t('Active:', 'Activo:', lang)} v{activeVersion?.versionNumber || '?'}
            </span>
          )}
          {policy?.activeVersionId && (
            <div className="dropdown">
              <button className="btn btn-outline-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" disabled={translating}>
                {translating ? <><span className="spinner-border spinner-border-sm me-1" />{t('Translating…', 'Traduciendo…', lang)}</> : `✨ ${t('Translate', 'Traducir', lang)}`}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><button className="dropdown-item" onClick={() => handleTranslate('es')}>→ {t('Spanish', 'Español', lang)}</button></li>
                <li><button className="dropdown-item" onClick={() => handleTranslate('en')}>→ {t('English', 'Inglés', lang)}</button></li>
              </ul>
            </div>
          )}
          <button className="btn btn-outline-danger btn-sm" onClick={handleArchive}>{t('Archive', 'Archivar', lang)}</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}<button className="btn-close float-end btn-sm" onClick={() => setError('')} /></div>}
      {translateDone && <div className="alert alert-success py-2 mb-3 d-flex align-items-center gap-2">{translateDone}<button className="btn-close btn-sm ms-auto" onClick={() => setTranslateDone('')} /></div>}

      {/* Draft editor */}
      {(() => {
        const isBilingual = policy?.documentLanguage === 'both' || !!editorContentEs;
        const activeEditorContent = editorLang === 'es' ? editorContentEs : editorContent;
        return (
          <div className="card mb-4">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <strong style={{ fontSize: 14 }}>
                  {draftVersion
                    ? `${t('Draft', 'Borrador', lang)} — v${draftVersion.versionNumber}`
                    : t('No draft version', 'Sin versión borrador', lang)}
                </strong>
                {draftVersion && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {saving ? t('Saving…', 'Guardando…', lang) : saved ? t('✓ Saved', '✓ Guardado', lang) : ''}
                  </span>
                )}
                {draftVersion && isBilingual && (
                  <div className="btn-group btn-group-sm ms-2" role="group">
                    <button type="button" className={`btn ${editorLang === 'en' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setEditorLang('en')}>🇺🇸 EN</button>
                    <button type="button" className={`btn ${editorLang === 'es' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setEditorLang('es')}>🇪🇸 ES</button>
                  </div>
                )}
              </div>
              <div className="d-flex gap-2 align-items-center">
                {!draftVersion && (
                  <button className="btn btn-outline-primary btn-sm" onClick={handleCreateDraft}>
                    + {t('New Draft', 'Nuevo Borrador', lang)}
                  </button>
                )}
                {draftVersion && (
                  <>
                    <button className="btn btn-outline-danger btn-sm" onClick={handleDiscardDraft} disabled={discarding} title={t('Discard this draft', 'Descartar este borrador', lang)}>
                      {discarding ? '…' : t('Discard', 'Descartar', lang)}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => saveDraft()} disabled={saving}>
                      {saving ? t('Saving…', 'Guardando…', lang) : t('Save Draft', 'Guardar Borrador', lang)}
                    </button>
                    <button className="btn btn-success btn-sm" onClick={handlePublish} disabled={publishing || !editorContent.trim()}>
                      {publishing ? t('Publishing…', 'Publicando…', lang) : t('Publish', 'Publicar', lang)}
                    </button>
                  </>
                )}
              </div>
            </div>

            {draftVersion ? (
              <div className="card-body p-0">
                <div className="d-flex gap-3 p-3 border-bottom bg-light flex-wrap">
                  <div style={{ minWidth: 160 }}>
                    <label className="form-label mb-1 fw-semibold" style={{ fontSize: 12 }}>{t('Revision Date', 'Fecha de Revisión', lang)}</label>
                    <input type="date" className="form-control form-control-sm" value={revisionDate}
                      onChange={e => setRevisionDate(e.target.value)} />
                  </div>
                  <div className="flex-grow-1">
                    <label className="form-label mb-1 fw-semibold" style={{ fontSize: 12 }}>{t('Version Notes', 'Notas de Versión', lang)}</label>
                    <input className="form-control form-control-sm" placeholder={t('What changed in this version?', '¿Qué cambió en esta versión?', lang)} value={versionNotes}
                      onChange={e => setVersionNotes(e.target.value)} />
                  </div>
                </div>
                {isBilingual && (
                  <div className="px-3 pt-2 pb-0" style={{ fontSize: 12, color: '#888' }}>
                    {editorLang === 'es'
                      ? t('Editing Spanish content — switch to 🇺🇸 EN to edit the English version', 'Editando contenido en español — cambia a 🇺🇸 EN para editar la versión en inglés', lang)
                      : t('Editing English content — switch to 🇪🇸 ES to edit the Spanish version', 'Editando contenido en inglés — cambia a 🇪🇸 ES para editar la versión en español', lang)
                    }
                  </div>
                )}
                <div>
                  <RichTextEditor
                    key={editorLang}
                    value={activeEditorContent}
                    onChange={handleContentChange}
                    onImageUpload={handleImageUpload}
                    minHeight={400}
                    stickyTop={70}
                  />
                </div>
              </div>
            ) : (
              <div className="card-body text-center text-muted py-5" style={{ fontSize: 14 }}>
                {policy?.activeVersionId
                  ? t('The active version is published. Create a new draft to make changes.', 'La versión activa está publicada. Cree un nuevo borrador para hacer cambios.', lang)
                  : t('No content yet. Create a draft to start writing the policy.', 'Sin contenido. Cree un borrador para comenzar a escribir la política.', lang)}
              </div>
            )}
          </div>
        );
      })()}

      {/* Version history */}
      {publishedVersions.length > 0 && (
        <div>
          <h6 className="text-muted mb-2" style={{ fontSize: 13 }}>{t('Published Versions', 'Versiones Publicadas', lang)}</h6>
          <table className="table table-sm table-bordered" style={{ fontSize: 12 }}>
            <thead className="table-light">
              <tr>
                <th>{t('Version', 'Versión', lang)}</th>
                <th>{t('Revision Date', 'Fecha de Revisión', lang)}</th>
                <th>{t('Published', 'Publicado', lang)}</th>
                <th>{t('Notes', 'Notas', lang)}</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {publishedVersions.map(v => (
                <tr key={v.id} className={v.id === policy?.activeVersionId ? 'table-success' : ''}>
                  <td>
                    v{v.versionNumber}
                    {v.id === policy?.activeVersionId && <span className="badge bg-success ms-1" style={{ fontSize: 9 }}>{t('active', 'activo', lang)}</span>}
                  </td>
                  <td>{v.revisionDate || '—'}</td>
                  <td>{v.publishedAt ? new Date(v.publishedAt).toLocaleDateString() : '—'}</td>
                  <td className="text-muted">{v.notes || '—'}</td>
                  <td>
                    {v.id !== policy?.activeVersionId && (
                      <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }}
                        onClick={() => setShowRestoreConfirm(v)}>
                        {t('Restore', 'Restaurar', lang)}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Restore version confirm modal ── */}
      {showRestoreConfirm && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title">{t('Restore this version?', '¿Restaurar esta versión?', lang)}</h5>
                <button className="btn-close" onClick={() => setShowRestoreConfirm(null)} />
              </div>
              <div className="modal-body pt-2">
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  {t(
                    `v${showRestoreConfirm.versionNumber} will become the active published version.`,
                    `La v${showRestoreConfirm.versionNumber} se convertirá en la versión publicada activa.`,
                    lang
                  )}
                  {showRestoreConfirm.revisionDate && (
                    <span className="ms-1 text-muted" style={{ fontSize: 13 }}>· {showRestoreConfirm.revisionDate}</span>
                  )}
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowRestoreConfirm(null)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-primary btn-sm" onClick={async () => {
                  const v = showRestoreConfirm;
                  setShowRestoreConfirm(null);
                  try {
                    await sqfService.publishPolicyVersion(id, v.id);
                    load();
                  } catch {
                    setError(t('Failed to restore version.', 'Error al restaurar la versión.', lang));
                  }
                }}>
                  {t('Restore', 'Restaurar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Discard draft confirm modal ── */}
      {showDiscardConfirm && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title">{t('Discard draft?', '¿Descartar el borrador?', lang)}</h5>
                <button className="btn-close" onClick={() => setShowDiscardConfirm(false)} />
              </div>
              <div className="modal-body pt-2">
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  {t(
                    'This draft will be permanently deleted. The published version will remain active.',
                    'Este borrador se eliminará permanentemente. La versión publicada seguirá activa.',
                    lang
                  )}
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowDiscardConfirm(false)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-danger btn-sm" onClick={confirmDiscard}>
                  {t('Discard Draft', 'Descartar Borrador', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Archive confirm modal ── */}
      {showArchiveConfirm && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title">{t('Archive this policy?', '¿Archivar esta política?', lang)}</h5>
                <button className="btn-close" onClick={() => setShowArchiveConfirm(false)} />
              </div>
              <div className="modal-body pt-2">
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  <strong>{(lang === 'es' && policy?.nameEs) ? policy.nameEs : policy?.name}</strong>
                  {' '}{t(
                    'will be archived and no longer appear in the active list.',
                    'será archivada y ya no aparecerá en la lista activa.',
                    lang
                  )}
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowArchiveConfirm(false)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-danger btn-sm" onClick={confirmArchive}>
                  {t('Archive', 'Archivar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Publish confirm modal ── */}
      {showPublishConfirm && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 440 }}>
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title">{t('Publish this version?', '¿Publicar esta versión?', lang)}</h5>
                <button className="btn-close" onClick={() => setShowPublishConfirm(false)} />
              </div>
              <div className="modal-body pt-2">
                <p className="text-muted mb-1" style={{ fontSize: 14 }}>
                  {t(
                    'This draft will become the active published version of this policy.',
                    'Este borrador se convertirá en la versión publicada activa de esta política.',
                    lang
                  )}
                </p>
                {revisionDate && (
                  <p className="mb-0" style={{ fontSize: 13 }}>
                    <strong>{t('Revision date:', 'Fecha de revisión:', lang)}</strong> {revisionDate}
                  </p>
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowPublishConfirm(false)}>
                  {t('Cancel', 'Cancelar', lang)}
                </button>
                <button className="btn btn-success btn-sm" onClick={confirmPublish} disabled={publishing}>
                  {publishing
                    ? <><span className="spinner-border spinner-border-sm me-1" />{t('Publishing…', 'Publicando…', lang)}</>
                    : t('Publish', 'Publicar', lang)
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
